import { useState, useEffect, useRef } from 'react';

/**
 * Pull-to-refresh gesture hook.
 *
 * Listens for touch events on `window`. When the user drags down from the top
 * of the page (scrollY === 0) by at least PULL_THRESHOLD pixels (72 px),
 * `onRefresh` is awaited and `refreshing` is set to `true` for the duration.
 *
 * The pull distance is damped with a square-root feel and capped at
 * 1.4 × PULL_THRESHOLD so the indicator never travels too far off-screen.
 *
 * Extracted from `src/pages/Sossial.tsx` to make the logic importable and
 * independently testable.
 */
export const PULL_THRESHOLD = 72; // px needed to trigger a refresh

export function usePullToRefresh(onRefresh: () => Promise<void>) {
  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const pulling = useRef(false);

  useEffect(() => {
    const onTouchStart = (e: TouchEvent) => {
      if (window.scrollY === 0) {
        startY.current = e.touches[0].clientY;
        pulling.current = true;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!pulling.current || refreshing) return;
      const delta = e.touches[0].clientY - startY.current;
      if (delta > 0) {
        // Dampen with square-root feel, cap at 1.4× threshold
        setPullY(Math.min(Math.sqrt(delta) * 4, PULL_THRESHOLD * 1.4));
      }
    };

    const onTouchEnd = async () => {
      if (!pulling.current) return;
      pulling.current = false;
      if (pullY >= PULL_THRESHOLD && !refreshing) {
        setRefreshing(true);
        setPullY(PULL_THRESHOLD); // hold indicator while refreshing
        await onRefresh();
        setRefreshing(false);
      }
      setPullY(0);
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [pullY, refreshing, onRefresh]);

  return { pullY, refreshing };
}
